import type {Context} from '../types';

export default (context: Context) => {
  const {
    node: {jsonNode},
  } = context;

  const {data = {}} = jsonNode;

  const isAlreadyMigrated =
    Array.isArray(jsonNode?.data?.texts) && Boolean(data.texts.length);

  const needsMigration =
    jsonNode.type === 'CommercePaypalCheckoutErrorMessage' &&
    !isAlreadyMigrated;

  return {
    needed: needsMigration,
    logData: {
      msg: 'Migrating CommercePaypalCheckoutErrorMessage node data shape',
      meta: {
        nodeId: jsonNode._id,
      },
    },
    apply: () => {
      const copy = {
        INFO_ERROR:
          'There was an error processing your customer info. Please try again, or contact us if you continue to have problems.',
        SHIPPING_ERROR:
          'Sorry. We can’t ship your order to the address provided.',
        ORDER_EXTRAS_ERROR:
          'A merchant setting has changed that impacts your cart. Please refresh and try again.',
        PRICING_ERROR:
          'The prices of one or more items in your cart have changed. Please refresh this page and try again.',
        PRODUCT_ERROR:
          'One or more of the products in your cart have been removed. Please refresh the page and try again.',
        PAYMENT_ERROR:
          'There was an error processing your payment. Please try again, or contact us if you continue to have problems.',
        BILLING_ERROR:
          'Your payment could not be completed with the payment information provided. Please make sure that your card and billing address information is correct, or try a different payment card, to complete this order. Contact us if you continue to have problems.',
        ORDER_MINIMUM_ERROR:
          'The order minimum was not met. Add more items to your cart to continue.',
        INVALID_DISCOUNT_ERROR: 'This discount is invalid.',
        EXPIRED_DISCOUNT_ERROR: 'This discount is no longer available.',
        USAGE_REACHED_DISCOUNT_ERROR: 'This discount is no longer available.',
        REQUIREMENTS_NOT_MET_DISCOUNT_ERROR:
          'Your order does not meet the requirements for this discount.',
      };

      type CopyKeys = keyof typeof copy;

      if (jsonNode.data) {
        if (jsonNode.data.textStateCopy) {
          for (const [key, value] of Object.entries(
            jsonNode.data.textStateCopy
          )) {
            if (key in copy) {
              copy[key as CopyKeys] = value as string;
            }
          }

          // eslint-disable-next-line webflow/no-delete
          delete jsonNode.data.textStateCopy;
        }
        if (jsonNode.data.commerce) {
          // delete the old data in the commerce field
          for (const key of Object.keys(jsonNode.data.commerce)) {
            if (key in copy) {
              // eslint-disable-next-line webflow/no-delete
              delete jsonNode.data.commerce[key as CopyKeys];
            }
          }
          if (Object.keys(jsonNode.data.commerce).length === 0) {
            // eslint-disable-next-line webflow/no-delete
            delete jsonNode.data.commerce;
          }
        }
      }

      const newData = {
        ...jsonNode.data,
        temp: {index: 0},
        texts: [
          {
            id: 'INFO_ERROR',
            name: 'General customer info error',
            copy: copy.INFO_ERROR,
            category: 'General Errors',
          },
          {
            id: 'SHIPPING_ERROR',
            name: 'Shipping not available',
            copy: copy.SHIPPING_ERROR,
            category: 'General Errors',
          },
          {
            id: 'ORDER_EXTRAS_ERROR',
            name: 'Merchant setting changed',
            copy: copy.ORDER_EXTRAS_ERROR,
            category: 'General Errors',
          },
          {
            id: 'PRICING_ERROR',
            name: 'Product price changed',
            copy: copy.PRICING_ERROR,
            category: 'Product Errors',
          },
          {
            id: 'PRODUCT_ERROR',
            name: 'Product removed',
            copy: copy.PRODUCT_ERROR,
            category: 'Product Errors',
          },
          {
            id: 'PAYMENT_ERROR',
            name: 'General payment error',
            copy: copy.PAYMENT_ERROR,
            category: 'Billing Errors',
          },
          {
            id: 'BILLING_ERROR',
            name: 'Card declined',
            copy: copy.BILLING_ERROR,
            category: 'Billing Errors',
          },
          {
            id: 'ORDER_MINIMUM_ERROR',
            name: 'Order minimum not met',
            copy: copy.ORDER_MINIMUM_ERROR,
            category: 'Billing Errors',
          },
          {
            id: 'INVALID_DISCOUNT_ERROR',
            name: 'Invalid discount error',
            copy: copy.INVALID_DISCOUNT_ERROR,
            category: 'Discount Errors',
          },
          {
            id: 'EXPIRED_DISCOUNT_ERROR',
            name: 'Discount expired',
            copy: copy.EXPIRED_DISCOUNT_ERROR,
            category: 'Discount Errors',
          },
          {
            id: 'USAGE_REACHED_DISCOUNT_ERROR',
            name: 'Discount usage limit reached',
            copy: copy.USAGE_REACHED_DISCOUNT_ERROR,
            category: 'Discount Errors',
          },
          {
            id: 'REQUIREMENTS_NOT_MET_DISCOUNT_ERROR',
            name: 'Discount requirements not met',
            copy: copy.REQUIREMENTS_NOT_MET_DISCOUNT_ERROR,
            category: 'Discount Errors',
          },
        ],
      };

      jsonNode.data = newData;
    },
  };
};
